import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Footer from '@/components/Footer';
import { CAREERS_JOBS } from '@/data/careersJobs';
import PageSeo from '@/components/PageSeo';

export default function CareerDetail() {
  const { slug } = useParams();
  const job = CAREERS_JOBS.find((j) => j.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!job) {
    return (
      <div className="min-h-screen bg-black text-white">
        <div className="mx-auto max-w-3xl px-6 py-12">
          <Link to="/careers" className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-8 text-sm">
            <ArrowLeft className="w-4 h-4" />
            Back to Open Positions
          </Link>
          <p className="text-white/60">This position could not be found.</p>
        </div>
      </div>
    );
  }

  const subject = `Job ${job.jobId || 'Application'}: ${job.title} Application`;
  const body = 'Please find my resume attached for your review.';
  const applyUrl = `mailto:careers@defera.app?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  return (
    <div className="min-h-screen bg-black text-white">
      <PageSeo path={`/careers/${job.slug}`} />
      <div className="mx-auto max-w-3xl px-6 py-12">
        <Link
          to="/careers"
          className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-10 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Open Positions
        </Link>

        <p className="text-white/50 text-sm mb-1">{job.department}</p>
        <h1 className="text-3xl md:text-4xl font-light text-white mb-2 tracking-tight">{job.title}</h1>
        <p className="text-white/60 text-sm mb-10">{job.location}</p>

        <div className="border border-white/15 rounded-lg p-6 mb-12">
          <h2 className="text-base font-medium text-white mb-2">Ready to apply?</h2>
          <p className="text-white/60 text-sm mb-4">
            Send your resume and a note to careers@defera.app
          </p>
          <a
            href={applyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block border border-white/30 px-5 py-2.5 text-sm text-white hover:bg-white/10 transition-colors"
          >
            Apply Now
          </a>
        </div>

        <p className="text-white/80 leading-relaxed mb-10">{job.intro}</p>

        <h3 className="text-sm font-normal text-white/70 uppercase tracking-wide mb-3">The team</h3>
        <p className="text-white/80 leading-relaxed mb-10">{job.team}</p>

        <h3 className="text-sm font-normal text-white/70 uppercase tracking-wide mb-3">You will</h3>
        <ul className="space-y-2 mb-10">
          {job.youWill.map((item, i) => (
            <li key={i} className="flex gap-2 text-white/80 text-sm">
              <span className="text-white/40">—</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <h3 className="text-sm font-normal text-white/70 uppercase tracking-wide mb-3">Ideally you would have</h3>
        <ul className="space-y-2 mb-10">
          {job.ideallyHave.map((item, i) => (
            <li key={i} className="flex gap-2 text-white/80 text-sm">
              <span className="text-white/40">—</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <h3 className="text-sm font-normal text-white/70 uppercase tracking-wide mb-3">Nice to haves</h3>
        <ul className="space-y-2 mb-10">
          {job.niceToHave.map((item, i) => (
            <li key={i} className="flex gap-2 text-white/70 text-sm">
              <span className="text-white/30">—</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <h3 className="text-sm font-normal text-white/70 uppercase tracking-wide mb-3">Compensation</h3>
        <p className="text-white/80 text-sm leading-relaxed mb-2">{job.compensation}</p>
        <p className="text-white/50 text-xs leading-relaxed mb-12">
          Benefits include health, dental, vision, PTO, and a learning stipend.
        </p>

        <div className="border-t border-white/10 pt-10">
          <h3 className="text-xs font-normal text-white/50 uppercase tracking-widest mb-4">About Defera</h3>
          <p className="text-white/80 text-sm leading-relaxed mb-4">
            We make air travel accessible through transparent pricing, flexible payments, and technology that removes barriers. Based in San Francisco, CA.
          </p>
          <p className="text-white/45 text-xs leading-relaxed">
            Equal opportunity employer. Reasonable accommodations available—contact careers@defera.app.
          </p>
        </div>

        <p className="mt-10 text-xs text-white/40">
          Defera LLC • San Francisco, CA
        </p>
      </div>

      <Footer />
    </div>
  );
}
